// StudyAbroadPricing.js
// PRICES NOT SUPPLIED BY IVIPASS: the $149 base fee, the +$20 per additional
// application and the $189 worked example below are inherited from the cloned
// site and have been left untouched on purpose. They are placeholders and must
// be confirmed or replaced by Ivipass before this page goes live.
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const StudyAbroadPricing = () => {
  const coverageItems = [
    'Drawing up a shortlist of universities and courses',
    'Putting the application together and filing it',
    'Working through drafts of your statement of purpose',
    'Getting your academic paperwork in order',
    'Chasing admissions and explaining whatever comes back'
  ];

  const importantNotes = [
    'Tuition is paid straight to the university',
    'Ivipass never handles tuition money',
    'Any scholarship or fee reduction comes from the institution itself',
    'Whether you are admitted is decided by the university alone'
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              What Study Abroad Support Costs
            </h2>
          </motion.div>

          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-surface p-8 rounded-lg mb-6"
          >
            <h3 className="text-xl font-semibold text-primary mb-4">
              Study Abroad Support
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              Fees here move with the destination, the number of applications you file, and how much of the work you would like us to take on.
            </p>

            {/* Coverage List */}
            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                A study abroad fee usually includes:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Fee Structure */}
            <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-primary mb-4">
                Fee Breakdown
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-primary">Standard Fee</p>
                    <p className="text-sm text-gray-600">Full support on a single university application</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">$149</p>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-base font-medium text-gray-700">Each Further Application</p>
                    <p className="text-sm text-gray-600">Added for every university beyond the first</p>
                  </div>
                  <p className="text-xl font-semibold text-gray-700">+$20</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Worked example:</span> three universities = $149 + ($20 × 2) = <span className="font-semibold text-primary">$189 total</span>
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              The final figure is agreed after your consultation, once we know your profile and the route you have chosen.
            </p>
          </motion.div>

          {/* Important Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-primary">
                Worth knowing:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StudyAbroadPricing;
